import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import {
  CURRENT_SOCKET_ID_VERSION,
  SOCKET_ID_VERSION_CONFIG_KEY,
} from "../../utils/dynamicSockets/dynamicSocketId";
// Imported from the module, not the package barrel: the barrel re-exports this
// step, and going through it would close a cycle.
import { resolveSwitchCaseSockets } from "../../utils/dynamicSockets/switchOutputSockets";
import { DecisionConditionSchema } from "./decision.step";

/**
 * A single case in the switch step configuration.
 *
 * The label is a display name. It used to double as the output socket id,
 * which is why it was constrained to socket-id shape; under
 * `__socketIdVersion: 2` the id is derived separately and marked, so the label
 * is free text. The old constraint still applies to steps on version 1, whose
 * persisted edges reference the bare label — see the configSchema check below.
 */
export const SwitchCaseSchema = z.object({
  label: z.string().min(1).describe("Branch name, shown on the output socket"),
  condition: DecisionConditionSchema.describe(
    "Condition to evaluate against the input value",
  ),
});

/** Label shape required when the label IS the socket id (version 1). */
const LEGACY_LABEL_REGEX = /^[a-z][a-z0-9_]*$/;

export type SwitchCase = z.infer<typeof SwitchCaseSchema>;

/**
 * Switch Step
 *
 * Multi-branch conditional routing. Evaluates an ordered list of conditions
 * against an input value and routes to the first matching case's output socket.
 * All non-matching cases (and optionally a default) receive SOCKET_NOT_TAKEN,
 * triggering skip propagation on their downstream branches.
 *
 * Output sockets are generated dynamically at template instantiation time
 * based on the configured cases — similar to how event_trigger generates
 * outputs from the selected event schema.
 *
 * Example workflow:
 * ```
 * ClassifyDocument → Switch(cases: [
 *                     { label: "invoice", condition: { operator: "equals", value: "invoice" } },
 *                     { label: "po",      condition: { operator: "equals", value: "purchase_order" } },
 *                     { label: "receipt",  condition: { operator: "equals", value: "receipt" } },
 *                   ])
 *                     ├─ [invoice] → ProcessInvoice
 *                     ├─ [po]      → ProcessPO
 *                     ├─ [receipt]  → ProcessReceipt
 *                     └─ [default]  → ManualReview
 * ```
 *
 * First match wins — evaluation stops at the first satisfied condition.
 * If no case matches and `hasDefault` is true, the `default` socket is taken.
 * With `multiMatch` enabled, every case is evaluated independently and each
 * matching case's socket is taken (default still only fires on zero matches).
 *
 * Gating a branch whose data comes from elsewhere: wire the case socket into
 * the target step's flow-control anchor (`__flow_control_in__`) and wire the
 * real data (e.g. the inbound email) directly from its producer. An untaken
 * case emits SOCKET_NOT_TAKEN, which dead-prunes any step whose
 * flow-control-in edge it feeds — so the switch decides WHICH branch runs
 * while each branch's data flows on ordinary edges.
 */
export const switchStep = defineStepConfig({
  type: "switch",
  name: "Switch",
  description: "Multi-branch conditional routing based on ordered conditions",
  category: "Control Flow",

  inputSchema: z.object({
    value: z
      .any()
      .describe("The value to evaluate against each case condition"),
  }),

  // Output sockets are generated dynamically at instantiation from config.cases.
  // Uses z.record() so that dynamic case keys survive Zod validation (z.object()
  // would strip unknown keys). The real socket definitions come from
  // instantiateTemplate's dynamic generation.
  outputSchema: z.record(z.string(), z.any()),

  configSchema: z
    .object({
      cases: z
        .array(SwitchCaseSchema)
        .min(1)
        .describe("Ordered list of cases to evaluate (first match wins)"),
      hasDefault: z
        .boolean()
        .default(true)
        .describe("Whether to include a default output for unmatched values"),
      multiMatch: z
        .boolean()
        .optional()
        .describe(
          "Evaluate every case independently — each matching case's socket is taken (default: first match wins). The `value` input becomes an array socket so an upstream array feeds the switch whole instead of fanning out.",
        ),
      // Declared so z.object() does not strip the stamp on parse. The value is
      // written once, when the step is created, and never changed after.
      [SOCKET_ID_VERSION_CONFIG_KEY]: z
        .number()
        .int()
        .optional()
        .describe(
          "Socket-id scheme this step was created under. Absent means the legacy scheme, where a case label IS its output socket id.",
        ),
    })
    .superRefine((config, ctx) => {
      const version = config[SOCKET_ID_VERSION_CONFIG_KEY] ?? 1;

      // Every case must end up with a socket of its own. Two cases whose labels
      // derive the same id (identical labels, or distinct ones that collide
      // once truncated to SOCKET_ID_MAX_LENGTH) leave the loser with no socket
      // to emit on, so the branch is unroutable — the canvas never draws it and
      // the handler has nowhere to put the value. Refuse the config instead of
      // shipping a switch with a dead branch.
      const { socketIdByCaseIndex } = resolveSwitchCaseSockets(config);
      socketIdByCaseIndex.forEach((socketId, index) => {
        if (socketId !== null) return;
        ctx.addIssue({
          code: "custom",
          message: `Case "${config.cases[index]?.label}" cannot be given its own output socket — another case already resolves to the same socket id. Give it a distinct label.`,
          path: ["cases", index, "label"],
        });
      });

      if (version >= CURRENT_SOCKET_ID_VERSION) return;
      // On version 1 the label is the socket id, so it has to be shaped like
      // one. Enforced here rather than on the field so version 2 steps can use
      // free text without the check having to know about versions twice.
      config.cases.forEach((switchCase, index) => {
        if (!LEGACY_LABEL_REGEX.test(switchCase.label)) {
          ctx.addIssue({
            code: "custom",
            message:
              "Label must be lowercase alphanumeric with underscores (valid socket ID)",
            path: ["cases", index, "label"],
          });
        }
      });
    }),

  emits: [] as const,
});
